<script lang="ts" setup>
import RexCardV3 from '@/rex-components/RexCardV3.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import RexSwitchV3 from '@/rex-components/RexSwitchV3.vue';
import RexModalLabel from '@/rex-components/rex-modal-label.vue';
import RexFormInput from '@/rex-components/rex-modal-input.vue';
import { CSSProperties, computed, onMounted, ref, watch } from 'vue';
import RexInfoLabel from '@/rex-components/rex-info-label.vue';
import { rdsServiceCreateEditFormRadioGroupOptions } from '@/components/Cloud/Dashboard/cloud.types';
import BaseIcon from '@/rex-components/BaseIcon.vue';
import RexRadioV3 from '@/rex-components/RexRadioV3.vue';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import { isEmpty, isEqual } from 'lodash';
import { useRoute } from 'vue-router';
import { notificationError, notificationSucess } from '@/utils/notification';
import { useI18n } from 'vue-i18n';
import { useAwsServiceDetails } from '@/components/Cloud/Hooks/useAwsServiceDetails';
import { useFetchGlobalFrequency } from '@/components/Cloud/Hooks/useFetchGlobalFrequency';
import { useUpdateServiceInfo } from '@/components/Cloud/Hooks/useAwsUpdateServiceInfo';
import { useInstallAwsServicesManually } from '@/components/Cloud/Hooks/useInstallAwsServicesManually';
import { Props, TypeCondition } from './FormTypes';

// TODO: get this from constant file after merging with ingress
const initData = {
  pullFrequency: '',
  global: false,
  enableMonitoring: false,
  globalPullFrequencyMins: '',
  tagsCondition: '',
  tags: [{}] as { key: string; value: string }[],
};

const props = defineProps<Props>();
const emit = defineEmits(['updateAllServices', 'close']);
const { params } = useRoute();
const { t } = useI18n();
const {
  data: serviceInfo,
  fetchAwsServiceDetails,
  isLoading: isAwsServiceInfoLoading,
} = useAwsServiceDetails();
const {
  data: globalPullFrequencyMins,
  fetchGlobalFrequency,
  isLoading: isFetchGlobalFrequencyLoading,
} = useFetchGlobalFrequency();
const { updateServiceInfo, isLoading: isUpdateServiceInfoLoading } =
  useUpdateServiceInfo();
const { installServiceManually, isLoading: isManuallyInstallLoading } =
  useInstallAwsServicesManually();

const isEdit = computed(() => props.type === TypeCondition.EDIT);

const isLoading = computed(
  () =>
    isFetchGlobalFrequencyLoading.value ||
    isAwsServiceInfoLoading.value ||
    isUpdateServiceInfoLoading.value ||
    isManuallyInstallLoading.value,
);

// TODO: get this from constant file after merging with ingress
const getCardBodyStyle = computed(
  (): CSSProperties => ({
    height: '420px',
    width: '100%',
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  }),
);

const radioGroupOptions = rdsServiceCreateEditFormRadioGroupOptions;

const userInputs = ref({ ...initData });
const initialValue = ref({ ...initData });
const errorState = ref({
  isPullFrequencyValueError: false,
  isPullFrequencyValidationError: false,
});

const handleIsGlobalToggling = (toggleValue) => {
  userInputs.value.global = toggleValue;
  userInputs.value.pullFrequency = userInputs.value.globalPullFrequencyMins;
};

const handleMonitorToggeling = (toggleValue) => {
  userInputs.value.enableMonitoring = toggleValue;
};

const submitFrom = async (submitAction, payload) => {
  await submitAction(payload);

  notificationSucess(
    props.type === TypeCondition.EDIT
      ? t('aws.elasticCacheServiceUpdated')
      : t('aws.awselasticacheConfiguredSuccessfully'),
  );
  emit('updateAllServices', 'elasticache', userInputs.value.enableMonitoring);
};

const isFormValid = () => {
  errorState.value = {
    isPullFrequencyValueError: false,
    isPullFrequencyValidationError: false,
  };

  if (
    isNaN(parseInt(userInputs.value.pullFrequency)) ||
    userInputs.value.pullFrequency?.includes?.('.') ||
    userInputs.value.pullFrequency?.includes?.('-')
  ) {
    errorState.value.isPullFrequencyValidationError = true;
    return false;
  }

  if (
    userInputs.value.pullFrequency === '0' ||
    !userInputs.value.pullFrequency
  ) {
    errorState.value.isPullFrequencyValueError = true;
    return false;
  }

  return true;
};

watch(serviceInfo, (currentValue) => {
  const tempData = JSON.stringify({
    ...currentValue,
  });

  userInputs.value = JSON.parse(tempData);
  initialValue.value = JSON.parse(tempData);
});

watch(globalPullFrequencyMins, (currentValue) => {
  if (currentValue.success) {
    userInputs.value.globalPullFrequencyMins =
      currentValue.globalPullFrequencyMins.toString();
    userInputs.value.pullFrequency =
      currentValue.globalPullFrequencyMins.toString();
    userInputs.value.enableMonitoring = true;
    userInputs.value.global = true;
    userInputs.value.tagsCondition = 'AND';
  }
});

const getInitialData = async () => {
  try {
    if (props.type === TypeCondition.EDIT) {
      fetchAwsServiceDetails('elasticache');
    } else {
      fetchGlobalFrequency();
    }
  } catch {
    notificationError(t('aws.formSubmissionFailed'));
  }
};

const handleSaveClick = () => {
  if (!isFormValid()) return;
  if (props.type === TypeCondition.EDIT) {
    submitFrom(updateServiceInfo, {
      subscriptionId: params.subscriptionId,
      serviceSlug: 'elasticache',
      meta: {},
      pullFrequencyMins:
        userInputs.value.pullFrequency ||
        userInputs.value.globalPullFrequencyMins,
      isGlobalEnabled: userInputs.value.global,
      tagsCondition: userInputs.value.tagsCondition,
      isMonitoringEnabled: userInputs.value.enableMonitoring,
      tags:
        Object.keys(userInputs.value.tags[0]).length > 0
          ? userInputs.value.tags.filter((tag) => !isEmpty(tag))
          : [],
    });
  } else {
    submitFrom(installServiceManually, {
      isAutoInstall: false,
      serviceSlug: 'elasticache',
      subscriptionId: params.subscriptionId,
      meta: {},
      tags:
        Object.keys(userInputs.value.tags[0]).length > 0
          ? userInputs.value.tags.filter((tag) => !isEmpty(tag))
          : [],
      tagsCondition: userInputs.value.tagsCondition,
      pullFrequencyMins:
        userInputs.value.pullFrequency ||
        userInputs.value.globalPullFrequencyMins,
      isMonitoringEnabled: userInputs.value.enableMonitoring,
    });
  }
};

onMounted(() => {
  getInitialData();
});
</script>

<template>
  <div class="lambda-create-edit-form">
    <RexSpinV3 :spinning="isLoading">
      <RexCardV3 :bodyStyle="getCardBodyStyle">
        <template #title>
          <p class="card-header-text">
            <span v-if="isEdit">
              {{ $t('aws.awsElasticacheEditForm') }}
            </span>
            <span v-else>
              {{ $t('aws.awsElasticache') }}
            </span>
          </p>
        </template>
        <div class="card-body">
          <div class="form-container">
            <div class="input-container">
              <div class="pull-frequency-container">
                <RexModalLabel
                  :primaryLabelStyle="{
                    color: userInputs.global ? 'rgba(89, 90, 89, 1)' : 'white',
                  }"
                  :primaryLabel="$t('aws.rdsFormFields.pullFrequency')"
                />

                <div class="toggle-container">
                  {{ $t('aws.rdsFormFields.global') }}
                  <RexSwitchV3
                    @onChange="handleIsGlobalToggling"
                    v-model:checked="userInputs.global"
                  />
                </div>
              </div>
              <div class="input-field-container">
                <RexFormInput
                  v-model="userInputs.pullFrequency"
                  type="text"
                  id="input-pull-frequency"
                  :disabled="userInputs.global"
                />
                <RexInfoLabel
                  :isError="true"
                  v-if="
                    userInputs.pullFrequency &&
                    errorState.isPullFrequencyValidationError &&
                    userInputs.pullFrequency !== '0'
                  "
                  :info="
                    $t(
                      'aws.createNewConfigguration.errorState.inValidPullFrequency',
                    )
                  "
                />
                <RexInfoLabel
                  :isError="true"
                  v-if="errorState.isPullFrequencyValueError"
                  :info="$t('aws.rdsFormFields.errorState.noPullFrequency')"
                />
              </div>
            </div>

            <div class="monitoring-container">
              {{ $t('aws.rdsFormFields.enableMonitoring') }}
              <RexSwitchV3
                size="small"
                @onChange="handleMonitorToggeling"
                v-model:checked="userInputs.enableMonitoring"
              />
            </div>

            <div class="tags-container py-4">
              <div class="tag-condition-radio-button">
                <RexModalLabel :primaryLabel="$t('aws.rdsFormFields.tags')" />
                <RexRadioV3
                  class="tags-condition"
                  v-model="userInputs.tagsCondition"
                  :options="radioGroupOptions"
                ></RexRadioV3>
              </div>

              <div class="tags-inputs">
                <div
                  v-for="(currentTag, index) in userInputs.tags"
                  :key="index"
                  class="tag-input-group"
                >
                  <RexFormInput
                    class="tag-input"
                    v-model="currentTag.key"
                    type="text"
                    id="input-tag-key"
                  />
                  <RexFormInput
                    class="tag-input"
                    v-model="currentTag.value"
                    type="text"
                    id="input-tag-value"
                  />
                  <RexButtonV3
                    type="primary"
                    danger
                    v-if="index + 1 !== userInputs.tags.length"
                    variant="primary"
                    id="remove-tag"
                    @onClick="userInputs.tags.splice(index, 1)"
                    class="tag-button"
                  >
                    <BaseIcon
                      height="0.938rem"
                      width="0.938rem"
                      name="deleteFilled"
                    />
                  </RexButtonV3>
                  <RexButtonV3
                    v-if="index + 1 === userInputs.tags.length"
                    variant="primary"
                    id="add-tag"
                    @onClick="
                      currentTag.key &&
                        currentTag.value &&
                        userInputs.tags.push({} as any)
                    "
                    class="tag-button"
                  >
                    <BaseIcon height="0.938rem" width="0.938rem" name="plus" />
                  </RexButtonV3>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <button
              @click="
                $emit(
                  'close',
                  props.type === TypeCondition.EDIT ? 'edit' : 'manualInstall',
                )
              "
              id="cancel"
              class="cancel-button"
            >
              {{ $t('aws.createNewConfigguration.cancel') }}
            </button>
            <RexButtonV3
              @onClick="handleSaveClick"
              id="save"
              :variant="
                isEqual(initialValue, userInputs) ? 'disabled' : 'primary'
              "
            >
              {{ $t('aws.createNewConfigguration.save') }}
            </RexButtonV3>
          </div>
        </div>
      </RexCardV3>
    </RexSpinV3>
  </div>
</template>

<style lang="scss" scoped>
.card-header-text {
  @include flex-center-vert;
  margin: 0;
  padding: 0.625rem 0;
}

.form-container {
  @include flex-column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.input-container {
  @include flex-column;
  width: 100%;
  align-items: start;
}

.pull-frequency-container {
  @include flex-center-justify(space-between);
  width: 100%;
  margin: 0.625rem 0rem;
}

.card-body {
  @include flex-center-justify(space-between);
  flex-direction: column;
  height: 100%;
}

.input-field-container {
  @include flex-column;
  width: 100%;
}

.toggle-container {
  @include flex-center-vert;
  gap: 0.313rem;
  font-weight: bold;
}

.monitoring-container {
  @include flex-center-vert;
  gap: 0.313rem;
  margin-top: 0.313rem;
  font-weight: bold;
}

.tags-container {
  @include flex-column;
  width: 100%;
}

.tags-inputs {
  @include flex-column;
  gap: 0.438rem;
  max-height: 6rem;
}

.tag-input-group {
  @include flex-center-justify(space-between);
  width: 100%;
}

.tag-input {
  width: 100%;
}

.tag-condition-radio-button {
  @include flex-center-justify(space-between);
  width: 100%;
  font-weight: bold;
}

.tag-button {
  width: 1.875rem;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
}

.endpint-url-container {
  @include flex-column;
  align-items: flex-start;
  width: 100%;
  margin: 0.625rem 0rem;
}

.cache-cluster-container {
  @include flex-column;
  align-items: flex-start;
  width: 100%;
  margin: 0.625rem 0rem;
}

.card-footer {
  @include flex-center-justify(flex-end);
  gap: 1rem;
  width: 100%;
  margin: 0.625rem 0rem;
}

.cancel-button {
  @include flex-center;
  background: none;
  border: none;
  cursor: pointer;
  color: $color-white;
}
</style>
